import React, { useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Space from "antd/lib/space";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import { CommonPatientExtensionItems } from "pages/patients/commonPatientExtensionItems";

import { PatientDetail } from "./patientDetail";
import { TasksTable } from "./tasksTable";

export interface Task {
  id: string;
  taskName: string;
  description: string;
  implementorRole: string;
  assignTo: string;
  status: "Not Started" | "In Progress" | "Completed";
}

export interface OrderGroup {
  orderName: string;
  templateId: string;
  tasks: Task[];
}

export const TasksPage: React.FC = () => {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const { id } = useParams<{ id: string }>();

  const userRole =
    sessionStorage.getItem("userRole") === "physician" ? "Doctor" : "Nurse";

  const [orders, setOrders] = useState<OrderGroup[]>(
    id === "2625"
      ? [
          {
            orderName: "NG Tube Procedure",
            templateId: "NGT-001",
            tasks: [
              {
                id: "T-001",
                taskName: "Clinical Suitability Review",
                description:
                  "Evaluate patient condition to confirm NG tube insertion is clinically appropriate.",
                implementorRole: "Doctor",
                assignTo: "ajit gite",
                status: "Not Started"
              },
              {
                id: "T-002",
                taskName: "Consent Documentation",
                description:
                  "Obtain patient or family consent and document consent form.",
                implementorRole: "Nurse",
                assignTo: "rachin ravi",
                status: "Not Started"
              },
              {
                id: "T-003",
                taskName: "NG Tube Preparation",
                description:
                  "Prepare equipment including NG tube, lubricant gel, syringe and gloves.",
                implementorRole: "Nurse",
                assignTo: "rachin ravi",
                status: "Not Started"
              },
              {
                id: "T-004",
                taskName: "NG Tube Insertion",
                description:
                  "Perform insertion according to clinical protocol.",
                implementorRole: "Nurse",
                assignTo: "rachin ravi",
                status: "Not Started"
              }
            ]
          }
        ]
      : []
  );

  const updateTaskStatus = (taskId: string, status: Task["status"]) => {
    setOrders(prev =>
      prev.map(order => ({
        ...order,
        tasks: order.tasks.map(t => (t.id === taskId ? { ...t, status } : t))
      }))
    );

    setSelectedTask(prev =>
      prev && prev.id === taskId ? { ...prev, status } : prev
    );
  };

  return (
    <>
      <CommonPatientExtensionItems />

      <Row>
        <Col>
          <TitleCol>{selectedTask ? "Task Details" : "Order Tasks"}</TitleCol>

          {selectedTask && (
            <Space style={{ padding: "0 0 20px 20px" }}>
              <AppButton
                type={ButtonType.Link}
                buttonContent={
                  <Space>
                    <ArrowLeftOutlined />
                    Back to Tasks
                  </Space>
                }
                onClick={() => setSelectedTask(null)}
              />
            </Space>
          )}
        </Col>
      </Row>

      {orders.length === 0 ? (
        <NoData>No Tasks Available</NoData>
      ) : selectedTask ? (
        <PatientDetail
          task={selectedTask}
          updateStatus={updateTaskStatus}
          userRole={userRole}
        />
      ) : (
        <TasksTable
          orders={orders}
          setSelectedTask={setSelectedTask}
          userRole={userRole}
        />
      )}
    </>
  );
};

const TitleCol = styled(Col)`
  padding: 20px;
  font-size: 18px;
  font-weight: bold;
`;

const NoData = styled.div`
  padding: 40px;
  text-align: center;
  color: #9e9e9e;
  font-size: 16px;
`;
